import { getInitialInflationLabel, parseInflationRate } from '@/utils/inflationParser';
import type { Quote } from '@client/model';
import { createQuoteWithROIandProjectedCoverage } from '@client/util/policyQuoteCalculations';
import { adjustQuoteForPremium } from '@client/util/quoteAdjustment';
import {
  benefitPeriodToString,
  benefitPeriodsMatch,
  getBenefitPeriodFromQuote,
  stringToBenefitPeriod,
  type BenefitPeriod,
} from '@client/utils/benefitPeriod';
import { calculateInvestmentBounds } from '@client/utils/investmentBounds';
import { getPaymentOptionFromQuote, type PaymentOption } from '@client/utils/quotePaymentOption';
import { formatROI } from '@shared';
import { useEffect, useMemo, useState } from 'react';
interface UseQuoteCustomizationProps {
  allCarrierQuotes: Quote[];
  topQuote: Quote;
  client: Client | undefined;
  returnOnInvestmentCalculationType?: ReturnOnInvestmentCalculationType;
}

interface AvailableOptions {
  benefitPeriods: BenefitPeriod[];
  inflationOptions: Array<{ label: string; rate: number }>;
  paymentTypes: PaymentOption[];
}

interface QuoteFilterParams {
  quote: Quote;
  selectedBenefitPeriod: BenefitPeriod;
  selectedInflationOption: string;
  selectedPaymentType: PaymentOption;
}

function matchesQuoteFilters({
  quote,
  selectedBenefitPeriod,
  selectedInflationOption,
  selectedPaymentType,
}: QuoteFilterParams): boolean {
  const quotePeriod = getBenefitPeriodFromQuote(quote);
  const periodMatches = benefitPeriodsMatch(quotePeriod, selectedBenefitPeriod);

  let inflationMatches = false;
  if (selectedInflationOption === 'None') {
    inflationMatches =
      (!quote.inflationBenefitOption || quote.inflationBenefitOption === 'None') &&
      (!quote.aobInflation || quote.aobInflation === 'None') &&
      (!quote.cobInflation || quote.cobInflation === 'None');
  } else {
    inflationMatches =
      quote.inflationBenefitOption === selectedInflationOption ||
      quote.aobInflation === selectedInflationOption ||
      quote.cobInflation === selectedInflationOption;
  }

  const paymentTypeMatches = getPaymentOptionFromQuote(quote) === selectedPaymentType;

  return periodMatches && inflationMatches && paymentTypeMatches;
}

export function useQuoteCustomization({
  allCarrierQuotes,
  topQuote,
  client,
  returnOnInvestmentCalculationType = 'total',
}: UseQuoteCustomizationProps) {
  const [isCustomizing, setIsCustomizing] = useState(false);

  // Extract available options from all carrier quotes
  const availableOptions = useMemo((): AvailableOptions => {
    const benefitPeriods = new Set<string>();
    const inflationOptions = new Map<string, number>();
    const paymentTypes = new Set<PaymentOption>();

    allCarrierQuotes.forEach(quote => {
      const period = getBenefitPeriodFromQuote(quote);
      if (period === 'lifetime' || period > 0) {
        benefitPeriods.add(benefitPeriodToString(period));
      }

      const inflationLabel = getInitialInflationLabel(quote);
      if (inflationLabel) {
        const rate = parseInflationRate(inflationLabel);
        inflationOptions.set(inflationLabel, rate);
      }

      paymentTypes.add(getPaymentOptionFromQuote(quote));
    });

    const sortedPeriods = Array.from(benefitPeriods)
      .map(stringToBenefitPeriod)
      .sort((a, b) => {
        if (a === 'lifetime') return 1;
        if (b === 'lifetime') return -1;
        return a - b;
      });

    return {
      benefitPeriods: sortedPeriods,
      inflationOptions: Array.from(inflationOptions.entries())
        .sort((a, b) => a[1] - b[1])
        .map(([label, rate]) => ({ label, rate })),
      paymentTypes: Array.from(paymentTypes).sort(),
    };
  }, [allCarrierQuotes]);

  const initialQuoteValues = useMemo(
    () => ({
      premium: topQuote.premiumAmount,
      benefitPeriod: getBenefitPeriodFromQuote(topQuote),
      inflationLabel: getInitialInflationLabel(topQuote),
      paymentType: getPaymentOptionFromQuote(topQuote),
    }),
    [topQuote],
  );

  const [selectedPremium, setSelectedPremium] = useState(initialQuoteValues.premium);
  const [selectedBenefitPeriod, setSelectedBenefitPeriod] = useState<BenefitPeriod>(
    initialQuoteValues.benefitPeriod,
  );
  const [selectedInflationOption, setSelectedInflationOption] = useState<string>(() => {
    const match = availableOptions.inflationOptions.find(
      opt => opt.label === initialQuoteValues.inflationLabel,
    );
    return match?.label || availableOptions.inflationOptions[0]?.label || 'None';
  });
  const [selectedPaymentType, setSelectedPaymentType] = useState<PaymentOption>(
    initialQuoteValues.paymentType,
  );

  const displayQuote = useMemo(() => {
    if (!client) return topQuote;

    const matchingQuote = allCarrierQuotes.find(quote =>
      matchesQuoteFilters({
        quote,
        selectedBenefitPeriod,
        selectedInflationOption,
        selectedPaymentType,
      }),
    );

    if (!matchingQuote) return topQuote;

    const premiumAdjustedQuote = adjustQuoteForPremium(matchingQuote, selectedPremium);

    try {
      const enriched = createQuoteWithROIandProjectedCoverage(
        premiumAdjustedQuote,
        client,
        returnOnInvestmentCalculationType,
      );
      return {
        ...enriched,
        formattedROI: formatROI(enriched.roiValues, returnOnInvestmentCalculationType),
      };
    } catch {
      return premiumAdjustedQuote;
    }
  }, [
    client,
    allCarrierQuotes,
    topQuote,
    selectedBenefitPeriod,
    selectedInflationOption,
    selectedPaymentType,
    selectedPremium,
    returnOnInvestmentCalculationType,
  ]);

  const matchingQuotesCount = useMemo(() => {
    return allCarrierQuotes.filter(quote =>
      matchesQuoteFilters({
        quote,
        selectedBenefitPeriod,
        selectedInflationOption,
        selectedPaymentType,
      }),
    ).length;
  }, [allCarrierQuotes, selectedBenefitPeriod, selectedInflationOption, selectedPaymentType]);

  const hasFilters = useMemo(() => {
    return (
      Math.abs(selectedPremium - initialQuoteValues.premium) > 100 ||
      !benefitPeriodsMatch(selectedBenefitPeriod, initialQuoteValues.benefitPeriod) ||
      selectedInflationOption !== initialQuoteValues.inflationLabel ||
      selectedPaymentType !== initialQuoteValues.paymentType
    );
  }, [
    selectedPremium,
    selectedBenefitPeriod,
    selectedInflationOption,
    selectedPaymentType,
    initialQuoteValues,
  ]);

  const resetFilters = () => {
    setSelectedPremium(initialQuoteValues.premium);
    setSelectedBenefitPeriod(initialQuoteValues.benefitPeriod);
    setSelectedInflationOption(initialQuoteValues.inflationLabel);
    setSelectedPaymentType(initialQuoteValues.paymentType);
  };

  const quotesForPaymentType = useMemo(
    () => allCarrierQuotes.filter(q => getPaymentOptionFromQuote(q) === selectedPaymentType),
    [allCarrierQuotes, selectedPaymentType],
  );

  const sliderBounds = useMemo(() => {
    return calculateInvestmentBounds(quotesForPaymentType, {
      min: topQuote.investmentMin || 10000,
      max: topQuote.investmentMax || 200000,
    });
  }, [quotesForPaymentType, topQuote]);

  // Reset premium slider to appropriate value when payment type changes
  useEffect(() => {
    if (quotesForPaymentType.length > 0) {
      const quote = quotesForPaymentType[0];
      const min = sliderBounds.min;
      const max = sliderBounds.max;
      const clampedPremium = Math.max(min, Math.min(max, quote.premiumAmount));
      setSelectedPremium(clampedPremium);
    }
  }, [quotesForPaymentType, sliderBounds.min, sliderBounds.max]);

  return {
    isCustomizing,
    setIsCustomizing,
    displayQuote,
    selectedPremium,
    setSelectedPremium,
    selectedBenefitPeriod,
    setSelectedBenefitPeriod,
    selectedInflationOption,
    setSelectedInflationOption,
    selectedPaymentType,
    setSelectedPaymentType,
    availableOptions,
    hasFilters,
    resetFilters,
    totalMatchingQuotes: matchingQuotesCount,
    sliderBounds,
  };
}
